/* eslint-disable no-nested-ternary */
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */
import { LogLevel } from "@azure/msal-browser";
import { getSubdomain } from "./utils/subDomainHelper";

/**
 * Enter here the user flows and custom policies for your B2C application
 * To learn more about user flows, visit: https://docs.microsoft.com/en-us/azure/active-directory-b2c/user-flow-overview
 * To learn more about custom policies, visit: https://docs.microsoft.com/en-us/azure/active-directory-b2c/custom-policy-overview
 */
export const b2cPolicies = {
  names: {
    signUpSignIn: "B2C_1_signupsignin1",
    forgotPassword: "B2C_1_reset_v3",
    editProfile: "B2C_1_EditProfile",
  },
  authorities: {
    signUpSignIn: {
      authority:
        "https://6thSenseRiskMultiTenantDev.b2clogin.com/6thSenseRiskMultiTenantDev.onmicrosoft.com/B2C_1_signupsignin1",
    },
    forgotPassword: {
      authority:
        "https://6thSenseRiskMultiTenantDev.b2clogin.com/6thSenseRiskMultiTenantDev.onmicrosoft.com/B2C_1_reset_v3",
    },
    editProfile: {
      authority:
        "https://6thSenseRiskMultiTenantDev.b2clogin.com/6thSenseRiskMultiTenantDev.onmicrosoft.com/B2C_1_EditProfile",
    },
  },
  authorityDomain: "6thSenseRiskMultiTenantDev.b2clogin.com",
};

const siteConfig = require("./siteConfig.json");

const subdomain = getSubdomain().toLowerCase();
export const siteData = siteConfig[subdomain];

function getApiScopesArray() {
  if (!siteData?.apiScopes) return ["openid", "profile"];
  return Array.isArray(siteData.apiScopes) ? siteData.apiScopes : [siteData.apiScopes];
}

/**
 * Configuration object to be passed to MSAL instance on creation.
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
 */
const authority =
  process.env.REACT_APP_ENTRA_AUTHORITY ??
  siteData?.authority ??
  siteData?.authTenantLogin ??
  b2cPolicies.authorities.signUpSignIn.authority;
const knownAuthorities =
  siteData?.knownAuthorities ??
  (authority ? [new URL(authority).hostname] : [b2cPolicies.authorityDomain]);

// Entra allows only HTTPS or http://localhost for redirect URIs.
// Derived from current origin when in browser; env/siteData only as override.
export function getRedirectUri() {
  if (typeof window !== "undefined") {
    const { hostname, port, protocol } = window.location;
    if (hostname === "localhost" || hostname === "127.0.0.1") {
      return `http://localhost:${port || "3000"}`;
    }
    if (protocol === "https:" || protocol === "http:") return window.location.origin;
  }
  if (process.env.REACT_APP_ENTRA_REDIRECT_URI) return process.env.REACT_APP_ENTRA_REDIRECT_URI;
  if (siteData?.redirectUri) return siteData.redirectUri;
  return "http://localhost:3000";
}

export const msalConfig = {
  auth: {
    clientId: process.env.REACT_APP_ENTRA_CLIENT_ID, // This is the ONLY mandatory field that you need to supply.
    authority,
    knownAuthorities,
    redirectUri: getRedirectUri(),
    postLogoutRedirectUri: getRedirectUri(),
    navigateToLoginRequestUrl: true,
  },
  cache: {
    cacheLocation: "sessionStorage", // Configures cache location. "sessionStorage" is more secure, but "localStorage" gives you SSO between tabs.
    storeAuthStateInCookie: false, // Set this to "true" if you are having issues on IE11 or Edge
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) {
          return;
        }
        switch (level) {
          case LogLevel.Error:
            console.error(message);
            return;
          case LogLevel.Info:
            console.info(message);
            return;
          case LogLevel.Verbose:
            console.debug(message);
            return;
          case LogLevel.Warning:
            console.warn(message);
            break;

          default:
        }
      },
    },
  },
};

/**
 * API base URL (should be the Risk API root including /api).
 * Precedence: REACT_APP_API_URL (local/staging override) → siteConfig.apiBaseUrl → same origin + /api.
 * So hosting the SPA on qantas.local while the API runs on localhost:5000 only needs .env REACT_APP_API_URL.
 */
export function getApiEndpoint() {
  const trimQuotes = (s) => (s || "").trim().replace(/^["']|["']$/g, "");
  const fromEnv = trimQuotes(process.env.REACT_APP_API_URL);
  const fromSite = trimQuotes(siteData?.apiBaseUrl);
  let base = fromEnv || fromSite;
  if (!base && typeof window !== "undefined" && window.location?.origin) {
    base = `${window.location.origin}/api`;
  }
  return base.replace(/\/$/, "");
}

/**
 * Add here the endpoints and scopes when obtaining an access token for protected web APIs. For more information, see:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/resources-and-scopes.md
 */
const apiScopesArray = getApiScopesArray();
export const protectedResources = {
  apiTodoList: {
    endpoint: getApiEndpoint(),
    scopes: {
      read: apiScopesArray,
      write: apiScopesArray,
    },
  },
};

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit:
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */
export const loginRequest = {
  scopes: getApiScopesArray(),
};

/**
 * When true, sign-in uses the native auth flow (email + password, no SSO).
 * When false, sign-in uses MSAL (redirect to Entra for SSO).
 * Set REACT_APP_USE_NATIVE_AUTH=false or siteData.useNativeAuth=false when the client requires SSO.
 */
export const useNativeAuthForSignIn =
  process.env.REACT_APP_USE_NATIVE_AUTH !== undefined
    ? process.env.REACT_APP_USE_NATIVE_AUTH === "true"
    : siteData?.useNativeAuth !== undefined
    ? Boolean(siteData.useNativeAuth)
    : true;

/**
 * Client ID for native auth API (sign-in/sign-up). Must be the public client app (e.g. pageriskapi).
 * Do not use the confidential/Graph client ID here or you get AADSTS550022.
 */
export const nativeAuthClientId =
  process.env.REACT_APP_NATIVE_AUTH_CLIENT_ID || msalConfig.auth.clientId || "";

// ── Dynamic per-tenant auth config ───────────────────────────────────────────

/**
 * Fetch the Entra config for this tenant from the API (GET /api/auth-config).
 * The API resolves the tenant by the Origin subdomain and returns only
 * public fields (clientId, authority, scopes, useNativeAuth).
 *
 * Returns an object with the same shape as msalConfig.auth + extra fields,
 * or null if the fetch fails (caller should fall back to static config).
 */
export async function loadTenantAuthConfig() {
  try {
    const apiBase = getApiEndpoint();
    const res = await fetch(`${apiBase}/auth-config`);
    if (!res.ok) return null;
    const json = await res.json();
    return json?.data || null;
  } catch {
    return null;
  }
}

/**
 * Build an MSAL PublicClientApplication config from a dynamically loaded
 * tenant config. Falls back field-by-field to the static msalConfig.
 */
export function buildMsalConfig(tenantConfig) {
  const dynamicAuthority = tenantConfig?.authority || msalConfig.auth.authority;
  const dynamicClientId = tenantConfig?.nativeAuthClientId || msalConfig.auth.clientId;
  const dynamicKnownAuthorities = dynamicAuthority
    ? [new URL(dynamicAuthority).hostname]
    : msalConfig.auth.knownAuthorities;

  return {
    ...msalConfig,
    auth: {
      ...msalConfig.auth,
      clientId: dynamicClientId,
      authority: dynamicAuthority,
      knownAuthorities: dynamicKnownAuthorities,
    },
  };
}
